import React, { ChangeEvent, FC } from 'react';
import { connect, getIn, FormikProps } from 'formik';
import { compose } from 'recompose';
import {
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  InputProps,
  TextFieldProps,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { findIndex } from 'lodash';

import { AllowedCounts, Content } from 'api';
import { DeleteIcon, SocialAccountIcon, ValidatedTextField } from 'components';
import { FACEBOOK_LIMIT, LINKEDIN_LIMIT, TWITTER_LIMIT } from 'constants/index';

interface IProps {
  allowedCounts: AllowedCounts;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<Content>;
}

interface ICommonProps {
  TextFieldProps: TextFieldProps;
  InputProps: InputProps;
  onChange: (event: ChangeEvent, onChange: (event: ChangeEvent) => void) => void;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(3),
    },
    formControl: {
      'label + &': {
        marginTop: 0,
      },
    },
    safe: {
      color: theme.custom.palette.grey1,
    },
    warning: {
      color: theme.custom.palette.orange,
    },
    error: {
      color: theme.custom.palette.red,
    },
  });

const Commentary: FC<IPropsEnhanced> = ({ classes, formik, allowedCounts }) => {
  const { t } = useTranslation();

  function getProps(): ICommonProps {
    return {
      TextFieldProps: { margin: 'none', multiline: true },
      InputProps: { classes: { formControl: classes.formControl } },
      onChange: (event, onChange) => {
        onChange(event);
      },
    };
  }

  function handleClearField(fieldName: string) {
    formik.setFieldValue(fieldName, '');
  }

  function renderClearField(fieldName: string) {
    return (
      <Grid item>
        <IconButton onClick={() => handleClearField(fieldName)}>
          <DeleteIcon />
        </IconButton>
      </Grid>
    );
  }

  function renderAdornment(length: number, limit: number) {
    const difference = limit - length;
    const className = classNames({
      [classes.safe]: difference >= 21,
      [classes.warning]: difference <= 20,
      [classes.error]: difference < 0,
    });

    return (
      <InputAdornment variant="filled" position="end">
        <Typography className={className}>
          {length}/{limit}
        </Typography>
      </InputAdornment>
    );
  }

  function indexOfNetwork(network: string) {
    const commentary = formik.values.commentary || [];
    let index = findIndex(commentary, ['network', network]);
    if (index < 0) {
      index = commentary.length;
      formik.setFieldValue(`commentary[${index}]`, { network, commentary: '' });
    }
    return index;
  }

  function renderTwitter() {
    const index = indexOfNetwork('twitter');
    const fieldName = `commentary[${index}].commentary`;
    const value = getIn(formik.values, fieldName) || '';
    const limit = allowedCounts.twitter || TWITTER_LIMIT;
    const twitterProps = getProps();

    twitterProps.InputProps.endAdornment = renderAdornment(value.length, limit);

    return (
      <Grid container alignItems="center">
        <Grid item>
          <SocialAccountIcon active provider="twitter" />
        </Grid>
        <Grid item xs>
          <ValidatedTextField name={fieldName} i18nKey="posting:commentary" {...twitterProps} />
        </Grid>
        {!!value && renderClearField(fieldName)}
      </Grid>
    );
  }

  function renderLinkedIn() {
    const index = indexOfNetwork('linkedin');
    const fieldName = `commentary[${index}].commentary`;
    const value = getIn(formik.values, fieldName) || '';
    const limit = allowedCounts.linkedin || LINKEDIN_LIMIT;
    const linkedInProps = getProps();

    linkedInProps.InputProps.endAdornment = renderAdornment(value.length, limit);

    return (
      <Grid container alignItems="center">
        <Grid item>
          <SocialAccountIcon active provider="linkedin" />
        </Grid>
        <Grid item xs>
          <ValidatedTextField name={fieldName} i18nKey="posting:commentary" {...linkedInProps} />
        </Grid>
        {!!value && renderClearField(fieldName)}
      </Grid>
    );
  }

  function renderFacebook() {
    const index = indexOfNetwork('facebook');
    const fieldName = `commentary[${index}].commentary`;
    const value = getIn(formik.values, fieldName) || '';
    const limit = allowedCounts.facebook || FACEBOOK_LIMIT;
    const facebookProps = getProps();

    facebookProps.InputProps.endAdornment = renderAdornment(value.length, limit);

    return (
      <Grid container alignItems="center">
        <Grid item>
          <SocialAccountIcon active provider="facebook" />
        </Grid>
        <Grid item xs>
          <ValidatedTextField name={fieldName} i18nKey="posting:commentary" {...facebookProps} />
        </Grid>
        {!!value && renderClearField(fieldName)}
      </Grid>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item xs>
          <Typography variant="subtitle2">{t('posting:commentary')}</Typography>
        </Grid>
        <Grid item xs>
          {renderTwitter()}
        </Grid>
        <Grid item xs>
          {renderLinkedIn()}
        </Grid>
        <Grid item xs>
          {renderFacebook()}
        </Grid>
      </Grid>
    </div>
  );
};

const EnhancedCommentary = compose<IPropsEnhanced, IProps>(connect, withStyles(styles))(Commentary);

export default EnhancedCommentary;
